import { graphql } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { sanitizeAndDecodeHtml } from '../helpers/htmlHelpers.ts';
import { type buildItemsGridDataQuery$data } from './__generated__/buildItemsGridDataQuery.graphql.ts';
import { type buildItemsGridDataMoreQuery$data } from './__generated__/buildItemsGridDataMoreQuery.graphql.ts';
import { type HpSharedItemsGridContent_item$key } from './components/__generated__/HpSharedItemsGridContent_item.graphql.ts';
import { type personalizedCollectionsTracking_item$key } from 'dibs-personalized/exports/tracking.ts';
import { type hpSharedItemsGridTracking_item$key } from './__generated__/hpSharedItemsGridTracking_item.graphql.ts';
import { type HpSharedItemsGridPersonalizedCollection_item$key } from './components/__generated__/HpSharedItemsGridPersonalizedCollection_item.graphql.ts';
import {
    MIN_EDITORIAL_COLLECTION_ITEMS_COUNT,
    PERSONALIZED_COLLECTION_POSITIONS,
    DESKTOP_INITIAL_EDITORS_PICKS_COUNT,
    getRecommendationGridCounts,
    YMAL_CATALOG_TYPE,
} from './constants.ts';
import {
    HP_SHARED_ITEMS_GRID_ITEM_TYPES,
    type HpSharedItemsGridData,
    type HpSharedItemsGridEditorialCollectionItem,
    type HpSharedItemsGridPersonalizedCollectionItem,
    type HpSharedItemsGridPersonalizedCollectionNestedItem,
    type HpSharedItemsGridRegularItem,
} from './types.ts';

export const buildItemsGridDataQuery = graphql`
    query buildItemsGridDataQuery(
        $consentedBuyerId: String = ""
        $userCountryCode: String!
        $priceBookName: String!
        $isTrade: Boolean!
        $guestId: String = ""
        $recentlyViewedItemIds: [String!]!
        $homepageId: String!
        $userType: String!
        $abTestList: [String] = []
        $editorsPicksCount: Int!
        $editorsPicksPageStartOffset: Int = 0
        $mostSavedCount: Int!
        $featuredItemsCount: Int!
        $useRecommendationItems: Boolean!
        $recommendationItemIds: [String!]!
    ) {
        viewer {
            ...HpSharedItemsGridContent_viewer
            ...SharedFavoritesProvider_viewer
            homepage(rootId: $homepageId, abTestList: $abTestList) {
                modules {
                    ... on EditorialModule {
                        type
                        items(userType: $userType) {
                            imageUrl
                            title
                            header
                            cta
                            linkData {
                                path
                            }
                        }
                    }
                }
            }
            editorsPicks: itemSearchItems(
                first: $editorsPicksCount
                page: 1
                pageStartOffset: $editorsPicksPageStartOffset
                uriRef: "/collections/editors-picks/"
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            ) @skip(if: $useRecommendationItems) {
                edges {
                    node {
                        serviceId
                        ...hpSharedItemsGridTracking_item
                        ...HpSharedItemsGridContent_item
                            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                    }
                }
            }
            mostSavedItems: itemSearchTrending(first: $mostSavedCount, module: MOST_SAVED_MODULE)
                @skip(if: $useRecommendationItems) {
                edges {
                    node {
                        serviceId
                        ...hpSharedItemsGridTracking_item
                        ...HpSharedItemsGridContent_item
                            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                    }
                }
            }
            recommendationItems: itemRead(itemIds: $recommendationItemIds)
                @include(if: $useRecommendationItems) {
                ... on Item {
                    __typename
                    serviceId
                    ...hpSharedItemsGridTracking_item
                    ...HpSharedItemsGridContent_item
                        @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                }
            }
            featuredItemsForYou: personalizedSponsoredItems(
                userId: $consentedBuyerId
                guestId: $guestId
                pageSize: $featuredItemsCount
            ) {
                # eslint-disable-next-line relay/unused-fields - metadata is used for sponsored tracking
                metadata {
                    ...useSponsoredItemsTracking_metadata
                }
                items {
                    serviceId
                    ...hpSharedItemsGridTracking_item
                    ...HpSharedItemsGridContent_item
                        @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                }
            }
            similarItemsByItemIds(itemIds: $recentlyViewedItemIds) {
                seedItemId
                items {
                    serviceId
                    ...HpSharedItemsGridPersonalizedCollection_item
                    ...personalizedCollectionsTracking_item
                }
            }
        }
    }
`;

export const buildItemsGridDataMoreQuery = graphql`
    query buildItemsGridDataMoreQuery(
        $priceBookName: String!
        $isTrade: Boolean!
        $homepageId: String!
        $userType: String!
        $abTestList: [String] = []
        $mostSavedCount: Int!
        $mostSavedPageStartOffset: Int!
        $excellentValuesCount: Int!
        $bestsellingDesignsCount: Int!
        $useRecommendationItems: Boolean!
        $recommendationItemIds: [String!]!
    ) {
        viewer {
            ...HpSharedItemsGridContent_viewer
            homepage(rootId: $homepageId, abTestList: $abTestList) {
                modules {
                    ... on EditorialModule {
                        type
                        items(userType: $userType) {
                            imageUrl
                            title
                            header
                            cta
                            linkData {
                                path
                            }
                        }
                    }
                }
            }
            mostSavedItems: itemSearchTrending(
                first: $mostSavedCount
                page: 1
                pageStartOffset: $mostSavedPageStartOffset
                module: MOST_SAVED_MODULE
            ) @skip(if: $useRecommendationItems) {
                edges {
                    node {
                        serviceId
                        ...hpSharedItemsGridTracking_item
                        ...HpSharedItemsGridContent_item
                            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                    }
                }
            }
            excellentValues: itemSearchTrending(
                first: $excellentValuesCount
                module: EXCELLENT_VALUES_MODULE
            ) @skip(if: $useRecommendationItems) {
                edges {
                    node {
                        serviceId
                        ...hpSharedItemsGridTracking_item
                        ...HpSharedItemsGridContent_item
                            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                    }
                }
            }
            bestsellingDesigns: itemSearchTrending(
                first: $bestsellingDesignsCount
                module: BEST_SELLING_MODULE
            ) @skip(if: $useRecommendationItems) {
                edges {
                    node {
                        serviceId
                        ...hpSharedItemsGridTracking_item
                        ...HpSharedItemsGridContent_item
                            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                    }
                }
            }
            recommendationItems: itemRead(itemIds: $recommendationItemIds)
                @include(if: $useRecommendationItems) {
                ... on Item {
                    __typename
                    serviceId
                    ...hpSharedItemsGridTracking_item
                    ...HpSharedItemsGridContent_item
                        @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                }
            }
        }
    }
`;

type ItemNode = HpSharedItemsGridContent_item$key &
    hpSharedItemsGridTracking_item$key & { serviceId?: string | null };

type PersonalizedCollectionItemNode = HpSharedItemsGridPersonalizedCollection_item$key &
    personalizedCollectionsTracking_item$key & {
        serviceId?: string | null;
    };

function createEmptyPersonalizedCollectionItem(
    collectionPositionIndex: number
): HpSharedItemsGridPersonalizedCollectionItem {
    return {
        type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.PERSONALIZED_COLLECTION,
        isEmpty: true,
        collectionId: `empty-pc-${collectionPositionIndex}`,
        collectionName: null,
        catalogType: null,
        collectionPositionIndex,
        items: [],
    };
}

function padArrayToLength<T>(
    items: readonly T[],
    length: number,
    createEmpty: (index: number) => T
): T[] {
    return Array.from({ length }, (_, index) => items[index] || createEmpty(index));
}

function mapPersonalizedCollectionItems(
    items: ReadonlyArray<PersonalizedCollectionItemNode | null> | null | undefined
): HpSharedItemsGridPersonalizedCollectionNestedItem[] {
    return (items || [])
        .map(itemRef => {
            const itemId = itemRef?.serviceId;
            if (!itemId || !itemRef) {
                return null;
            }
            return { itemId, itemRef };
        })
        .filter(filterFalsy);
}

function mapConnectionItems(
    edges: ReadonlyArray<{ node?: ItemNode | null } | null> | null | undefined,
    type: HpSharedItemsGridRegularItem['type']
): HpSharedItemsGridRegularItem[] {
    return (edges || [])
        .map(edge => {
            const itemRef = edge?.node;
            const itemId = itemRef?.serviceId;
            if (!itemId || !itemRef) {
                return null;
            }
            return { itemId, type, itemRef };
        })
        .filter((item): item is HpSharedItemsGridRegularItem => item !== null);
}

function mapFeaturedItems(
    items: ReadonlyArray<ItemNode | null> | null | undefined
): HpSharedItemsGridRegularItem[] {
    return (items || [])
        .map(itemRef => {
            const itemId = itemRef?.serviceId;
            if (!itemId || !itemRef) {
                return null;
            }
            return {
                itemId,
                type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.FEATURED_FOR_YOU,
                itemRef,
            };
        })
        .filter(filterFalsy);
}

type RecommendationItemNode = NonNullable<
    NonNullable<
        | buildItemsGridDataQuery$data['viewer']['recommendationItems']
        | buildItemsGridDataMoreQuery$data['viewer']['recommendationItems']
    >[number]
>;

function isRecommendationItemNode(
    item: RecommendationItemNode | null | undefined
): item is Extract<RecommendationItemNode, { readonly __typename: 'Item' }> {
    return item?.__typename === 'Item';
}

function mapRecommendationItems(
    items: ReadonlyArray<RecommendationItemNode | null> | null | undefined
): HpSharedItemsGridRegularItem[] {
    return (items || [])
        .map(itemRef => {
            if (!isRecommendationItemNode(itemRef)) {
                return null;
            }
            const itemId = itemRef.serviceId;
            if (!itemId) {
                return null;
            }
            return {
                itemId,
                type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.USER_RECOMMENDATIONS,
                itemRef,
            };
        })
        .filter(filterFalsy);
}

type EditorialModuleViewer =
    | buildItemsGridDataQuery$data['viewer']
    | buildItemsGridDataMoreQuery$data['viewer'];

function getEditorialItemsFromViewer(
    viewer: EditorialModuleViewer
): HpSharedItemsGridEditorialCollectionItem[] {
    const editorialCollectionItems: HpSharedItemsGridEditorialCollectionItem[] = [];
    const editorialModuleOrder = 2;

    const editorialModules = (viewer.homepage?.modules || []).filter(
        module => module?.type === 'editorial'
    );
    const secondEditorialModule = editorialModules[1];

    if (!secondEditorialModule) {
        return [];
    }

    (secondEditorialModule.items || [])
        .filter(filterFalsy)
        .forEach((editorialItem, editorialItemIndex) => {
            const imageUrl = editorialItem.imageUrl;
            const title = editorialItem.title;
            const linkPath = editorialItem.linkData?.path;
            const cta = editorialItem.cta;

            if (!imageUrl || !title || !linkPath || !cta) {
                return;
            }

            editorialCollectionItems.push({
                type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORIAL_COLLECTION,
                imageUrl,
                title: sanitizeAndDecodeHtml(title),
                linkPath,
                editorialModuleOrder,
                editorialItemIndex,
                cta,
                header: sanitizeAndDecodeHtml(editorialItem.header).trim() || null,
            });
        });

    return editorialCollectionItems;
}

function buildInitialEditorialCollectionItems(
    viewer: buildItemsGridDataQuery$data['viewer']
): HpSharedItemsGridEditorialCollectionItem[] {
    const editorialCollectionItems = getEditorialItemsFromViewer(viewer);

    if (editorialCollectionItems.length < MIN_EDITORIAL_COLLECTION_ITEMS_COUNT) {
        return [];
    }

    return editorialCollectionItems.slice(0, 1);
}

function buildLoadMoreEditorialCollectionItems(
    viewer: buildItemsGridDataMoreQuery$data['viewer']
): HpSharedItemsGridEditorialCollectionItem[] {
    const editorialCollectionItems = getEditorialItemsFromViewer(viewer);

    if (editorialCollectionItems.length < MIN_EDITORIAL_COLLECTION_ITEMS_COUNT) {
        return [];
    }

    return editorialCollectionItems.slice(1);
}

function buildPersonalizedCollectionItems(
    viewer: buildItemsGridDataQuery$data['viewer']
): HpSharedItemsGridPersonalizedCollectionItem[] {
    return padArrayToLength(
        (viewer.similarItemsByItemIds || [])
            .filter(filterFalsy)
            .map((rail, index) => {
                const seedItemId = rail.seedItemId;
                const items = mapPersonalizedCollectionItems(rail.items);

                if (!seedItemId || items.length === 0) {
                    return createEmptyPersonalizedCollectionItem(index);
                }

                return {
                    type: HP_SHARED_ITEMS_GRID_ITEM_TYPES.PERSONALIZED_COLLECTION,
                    collectionId: `ymal-${seedItemId}`,
                    collectionName: null,
                    catalogType: YMAL_CATALOG_TYPE,
                    collectionPositionIndex: index,
                    seedItemId,
                    items,
                };
            })
            .filter(filterFalsy),
        PERSONALIZED_COLLECTION_POSITIONS.length,
        createEmptyPersonalizedCollectionItem
    );
}

type BuildItemsGridDataOptions = {
    useRecommendationItems?: boolean;
    isMobile?: boolean;
};

export function buildInitialItemsGridData(
    viewer: buildItemsGridDataQuery$data['viewer'],
    { useRecommendationItems = false, isMobile = false }: BuildItemsGridDataOptions = {}
): HpSharedItemsGridData {
    const personalizedCollectionItems = buildPersonalizedCollectionItems(viewer);

    if (useRecommendationItems) {
        const { initialCount, fallbackCount } = getRecommendationGridCounts(isMobile);
        const recommendationItems = viewer.recommendationItems || [];
        const regularRecommendationItems = mapRecommendationItems(
            recommendationItems.slice(0, initialCount)
        );
        const fallbackItems = mapRecommendationItems(
            recommendationItems.slice(initialCount, initialCount + fallbackCount)
        );

        return {
            regularItems: [
                ...regularRecommendationItems,
                ...mapFeaturedItems(viewer.featuredItemsForYou?.items),
            ],
            fallbackItems,
            personalizedCollectionItems,
            editorialCollectionItems: buildInitialEditorialCollectionItems(viewer),
        };
    }

    const editorsPicksItems = mapConnectionItems(
        viewer.editorsPicks?.edges,
        HP_SHARED_ITEMS_GRID_ITEM_TYPES.EDITORS_PICKS
    );
    const regularEditorsPicksItems = editorsPicksItems.slice(
        0,
        DESKTOP_INITIAL_EDITORS_PICKS_COUNT
    );
    const fallbackEditorsPicksItems = editorsPicksItems.slice(DESKTOP_INITIAL_EDITORS_PICKS_COUNT);

    const regularItems: HpSharedItemsGridRegularItem[] = [
        ...regularEditorsPicksItems,
        ...mapFeaturedItems(viewer.featuredItemsForYou?.items),
        ...mapConnectionItems(
            viewer.mostSavedItems?.edges,
            HP_SHARED_ITEMS_GRID_ITEM_TYPES.MOST_SAVED
        ),
    ];

    return {
        regularItems,
        fallbackItems: fallbackEditorsPicksItems,
        personalizedCollectionItems,
        editorialCollectionItems: buildInitialEditorialCollectionItems(viewer),
    };
}

export function buildLoadMoreItemsGridData(
    viewer: buildItemsGridDataMoreQuery$data['viewer'],
    { useRecommendationItems = false }: BuildItemsGridDataOptions = {}
): HpSharedItemsGridData {
    if (useRecommendationItems) {
        return {
            regularItems: mapRecommendationItems(viewer.recommendationItems),
            fallbackItems: [],
            personalizedCollectionItems: [],
            editorialCollectionItems: buildLoadMoreEditorialCollectionItems(viewer),
        };
    }

    const regularItems: HpSharedItemsGridRegularItem[] = [
        ...mapConnectionItems(
            viewer.mostSavedItems?.edges,
            HP_SHARED_ITEMS_GRID_ITEM_TYPES.MOST_SAVED
        ),
        ...mapConnectionItems(
            viewer.excellentValues?.edges,
            HP_SHARED_ITEMS_GRID_ITEM_TYPES.EXCELLENT_VALUES
        ),
        ...mapConnectionItems(
            viewer.bestsellingDesigns?.edges,
            HP_SHARED_ITEMS_GRID_ITEM_TYPES.BESTSELLING_DESIGNS
        ),
    ];

    return {
        regularItems,
        fallbackItems: [],
        personalizedCollectionItems: [],
        editorialCollectionItems: buildLoadMoreEditorialCollectionItems(viewer),
    };
}
